/**
 * ProjectStageNav — persistent horizontal stage navigation bar for all project pages.
 *
 * Shows all workflow stages. Completed stages are clickable (navigate freely).
 * Future stages are visible but muted. Current stage is highlighted.
 * Always includes an escape hatch back to Recent Projects.
 */
import { useNavigate, useParams } from 'react-router-dom'
import { ChevronRight, Home } from 'lucide-react'
import type { ProjectStage } from '@/lib/contracts'
import { stageRoute } from '@/features/projects/pages/Home'

interface Stage {
  key: ProjectStage
  label: string
  route: string
  short: string
}

// 5-stage model: deep probe merged into Intake Session
const STAGES: Stage[] = [
  { key: 'upload',        label: 'Upload Docs',   short: 'Upload',   route: 'upload' },
  { key: 'intake',        label: 'Intake',         short: 'Intake',   route: 'intake' },
  { key: 'confirmation',  label: 'Confirmation',   short: 'Confirm',  route: 'confirmation' },
  { key: 'outputs',       label: 'Outputs',        short: 'Outputs',  route: 'output' },
  { key: 'dashboard',     label: 'Pipeline',       short: 'Pipeline', route: 'dashboard' },
]

// Order used for "is completed" calculation — legacy stages map to nearest equivalent
const STAGE_ORDER: ProjectStage[] = [
  'upload', 'context-review', 'intake', 'deep-probe', 'confirmation', 'outputs', 'dashboard',
]

// Maps legacy/internal stages to the 5 visible tabs
const STAGE_TAB_MAP: Partial<Record<ProjectStage, ProjectStage>> = {
  'context-review': 'upload',
  'deep-probe': 'intake',
}

const stageIndex = (stage: ProjectStage): number => STAGE_ORDER.indexOf(stage)

interface ProjectStageNavProps {
  currentStage: ProjectStage
  projectTitle?: string
}

export function ProjectStageNav({ currentStage, projectTitle }: ProjectStageNavProps) {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const projectId = id ?? ''
  // Map legacy stages to their 5-stage equivalent for tab highlighting
  const resolvedStage: ProjectStage = (STAGE_TAB_MAP[currentStage] ?? currentStage) as ProjectStage
  const currentIdx = stageIndex(currentStage)

  return (
    <div className="border-b border-[#233D4C] bg-[#02182B] sticky top-[65px] z-30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Top row: breadcrumb */}
        <div className="flex items-center gap-2 py-2 text-xs text-gray-500 border-b border-[#233D4C]/50">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-1 hover:text-gray-300 transition-colors"
          >
            <Home size={11} />
            Projects
          </button>
          <ChevronRight size={11} />
          <span className="text-gray-300 truncate max-w-[200px]">
            {projectTitle ?? projectId}
          </span>
        </div>

        {/* Stage tabs */}
        <nav className="flex items-center gap-0 overflow-x-auto py-0 scrollbar-hide">
          {STAGES.map((stage, i) => {
            const stageIdx = stageIndex(stage.key)
            const isActive = stage.key === resolvedStage
            const isCompleted = stageIdx < currentIdx
            const isFuture = stageIdx > currentIdx && !isActive
            const isClickable = isCompleted || isActive

            return (
              <button
                key={stage.key}
                disabled={isFuture && !isCompleted}
                onClick={() => {
                  if (isClickable) {
                    navigate(`/project/${projectId}/${stageRoute[stage.key]}`)
                  }
                }}
                className={`
                  relative flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap
                  border-b-2 transition-colors
                  ${isActive
                    ? 'border-[#FD802E] text-[#FD802E]'
                    : isCompleted
                      ? 'border-transparent text-gray-300 hover:text-white hover:border-gray-500 cursor-pointer'
                      : 'border-transparent text-gray-600 cursor-default'
                  }
                `}
              >
                {/* Step indicator */}
                <span className={`
                  w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold shrink-0
                  ${isActive
                    ? 'bg-[#FD802E] text-[#233D4C]'
                    : isCompleted
                      ? 'bg-green-500/20 text-green-400'
                      : 'bg-[#233D4C] text-gray-600'
                  }
                `}>
                  {isCompleted ? '✓' : i + 1}
                </span>
                <span className="hidden sm:block">{stage.label}</span>
                <span className="sm:hidden">{stage.short}</span>
              </button>
            )
          })}
        </nav>
      </div>
    </div>
  )
}
